import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { analyticsConfigured } from '../data/analytics';
import { ConsentContext, type ConsentContextValue } from './consentContext';
import { globalPrivacyControlActive, readConsent, writeConsent, type ConsentDecision } from './consentStore';

export function ConsentProvider({ children }: { children: ReactNode }) {
    const [ready, setReady] = useState(false);
    const [decision, setDecision] = useState<ConsentDecision | null>(null);
    const [bannerOpen, setBannerOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);

    useEffect(() => {
        const stored = readConsent();
        if (stored) {
            setDecision(stored);
        } else if (globalPrivacyControlActive()) {
            setDecision(writeConsent(false));
        } else {
            setBannerOpen(analyticsConfigured);
        }
        setReady(true);
    }, []);

    const decide = useCallback((analytics: boolean) => {
        setDecision(writeConsent(analytics));
        setBannerOpen(false);
        setSettingsOpen(false);
    }, []);

    const value = useMemo<ConsentContextValue>(() => ({
        ready,
        decision,
        bannerOpen,
        settingsOpen,
        acceptAll: () => decide(true),
        rejectAll: () => decide(false),
        save: decide,
        openSettings: () => {
            setSettingsOpen(true);
            setBannerOpen(true);
        }
    }), [ready, decision, bannerOpen, settingsOpen, decide]);

    return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}
